import { db } from '../database/db'
import conflictResolver, { generateUUID } from '../utils/conflictResolver'
import authService from './authService'
import managerService from './managerService'

class DexieService {
  get db() {
    return db
  }

  // Récupère le shopId de la session active
  getCurrentShopId() {
    const shopId = authService.getShopId()
    if (!shopId) {
      console.warn('[DexieService] Aucun shopId trouvé dans la session')
    }
    return shopId
  }

  // PRODUITS
  async addProduct(product) {
    try {
      const shopId = this.getCurrentShopId()
      const managerId = managerService.getCurrentManagerId()
      if (!shopId) {
        throw new Error('Session invalide: aucun shopId')
      }

      const { id, ...productData } = product

      // Ajouter shopId
      productData.shopId = shopId
      productData.managerId = managerId

      // Assurer UUID et timestamps
      if (!productData.uuid) {
        productData.uuid = generateUUID()
      }
      const now = new Date().toISOString()
      if (!productData.createdAt) productData.createdAt = now
      productData.updatedAt = now

      // Vérifier conflit de code dans la boutique actuelle
      const existingCode = await db.products
        .where('[shopId+code]')
        .equals([shopId, productData.code])
        .first()

      if (existingCode) {
        productData.code = await conflictResolver.generateUniqueProductCode(
          productData.code,
          shopId,
        )
      }

      const newId = await db.products.add(productData)
      return newId
    } catch (error) {
      console.error('Erreur ajout produit:', error)
      throw error
    }
  }

  async updateProduct(id, product) {
    try {
      const shopId = this.getCurrentShopId()
      const managerId = managerService.getCurrentManagerId()
      if (!shopId) return false

      const { id: _, ...productData } = product

      // Assurer que shopId ne peut pas être modifié
      productData.shopId = shopId
      productData.managerId = managerId
      productData.updatedAt = new Date().toISOString()

      await db.products.update(id, productData)
      return true
    } catch (error) {
      console.error('Erreur modification produit:', error)
      return false
    }
  }

  async getAllProducts() {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return []

      return await db.products
        .where('shopId')
        .equals(shopId)
        .reverse()
        .sortBy('createdAt')
    } catch (error) {
      console.error('Erreur chargement produits:', error)
      return []
    }
  }

  async deleteProduct(id) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      // Vérifier que le produit appartient à la boutique
      const product = await db.products.get(id)
      if (!product || product.shopId !== shopId) {
        console.warn('Tentative de suppression produit autre boutique')
        return false
      }

      await db.products.delete(id)
      return true
    } catch (error) {
      console.error('Erreur suppression produit:', error)
      return false
    }
  }

  async saveProducts(products) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      // Ajouter shopId à tous les produits
      const productsWithShopId = products.map((p) => ({
        ...p,
        shopId,
      }))

      await db.products.bulkPut(productsWithShopId)
      return true
    } catch (error) {
      console.error('Erreur sauvegarde produits:', error)
      return false
    }
  }

  // ACHATS
  async addPurchase(purchase) {
    try {
      const shopId = this.getCurrentShopId()
      const managerId = managerService.getCurrentManagerId()
      if (!shopId) {
        throw new Error('Session invalide: aucun shopId')
      }

      const { id, ...purchaseData } = purchase

      // Ajouter shopId
      purchaseData.shopId = shopId
      purchaseData.managerId = managerId

      // Assurer UUID et timestamps
      if (!purchaseData.uuid) {
        purchaseData.uuid = generateUUID()
      }
      const now = new Date().toISOString()
      if (!purchaseData.createdAt) purchaseData.createdAt = now
      purchaseData.updatedAt = now

      const newId = await db.purchases.add(purchaseData)
      return newId
    } catch (error) {
      console.error('Erreur ajout achat:', error)
      throw error
    }
  }

  async updatePurchase(id, purchase) {
    try {
      const shopId = this.getCurrentShopId()
      const managerId = managerService.getCurrentManagerId()
      if (!shopId) return false

      const { id: _, ...purchaseData } = purchase

      // Assurer que shopId ne peut pas être modifié
      purchaseData.shopId = shopId
      purchaseData.managerId = managerId
      purchaseData.updatedAt = new Date().toISOString()

      await db.purchases.update(id, purchaseData)
      return true
    } catch (error) {
      console.error('Erreur modification achat:', error)
      return false
    }
  }

  async getAllPurchases() {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return []

      return await db.purchases
        .where('shopId')
        .equals(shopId)
        .reverse()
        .sortBy('date')
    } catch (error) {
      console.error('Erreur chargement achats:', error)
      return []
    }
  }

  async savePurchases(purchases) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      // Ajouter shopId à tous les achats
      const purchasesWithShopId = purchases.map((p) => ({
        ...p,
        shopId,
      }))

      await db.purchases.bulkPut(purchasesWithShopId)
      return true
    } catch (error) {
      console.error('Erreur sauvegarde achats:', error)
      return false
    }
  }

  // MÉTADONNÉES (par boutique)
  async saveMetadata(key, value) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      const metadataKey = `${shopId}_${key}`
      await db.metadata.put({ key: metadataKey, value })
      return true
    } catch (error) {
      console.error('Erreur sauvegarde métadonnées:', error)
      return false
    }
  }

  async getMetadata(key) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return null

      const metadataKey = `${shopId}_${key}`
      const result = await db.metadata.get(metadataKey)
      return result ? result.value : null
    } catch (error) {
      console.error('Erreur chargement métadonnées:', error)
      return null
    }
  }

  // INFORMATIONS BOUTIQUE
  async saveShopInfo(shopInfo) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      await db.shopInfo.put({ id: shopId, ...shopInfo })
      return true
    } catch (error) {
      console.error('Erreur sauvegarde boutique:', error)
      return false
    }
  }

  async getShopInfo() {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return null

      return await db.shopInfo.get(shopId)
    } catch (error) {
      console.error('Erreur chargement boutique:', error)
      return null
    }
  }

  // RECHERCHE (par boutique)
  async searchProducts(query) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return []

      const lowerQuery = query.toLowerCase()
      return await db.products
        .where('shopId')
        .equals(shopId)
        .filter(
          (product) =>
            product.name.toLowerCase().includes(lowerQuery) ||
            product.code.toLowerCase().includes(lowerQuery),
        )
        .toArray()
    } catch (error) {
      console.error('Erreur recherche produits:', error)
      return []
    }
  }

  async searchPurchases(query) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return []

      const lowerQuery = query.toLowerCase()
      return await db.purchases
        .where('shopId')
        .equals(shopId)
        .filter(
          (purchase) =>
            purchase.clientName.toLowerCase().includes(lowerQuery) ||
            (purchase.clientPhone && purchase.clientPhone.includes(query)),
        )
        .toArray()
    } catch (error) {
      console.error('Erreur recherche achats:', error)
      return []
    }
  }

  // STATISTIQUES (par boutique)
  async getStats() {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) {
        return { productsCount: 0, purchasesCount: 0, totalRevenue: 0 }
      }

      const productsCount = await db.products
        .where('shopId')
        .equals(shopId)
        .count()
      const purchasesCount = await db.purchases
        .where('shopId')
        .equals(shopId)
        .count()

      const purchases = await db.purchases
        .where('shopId')
        .equals(shopId)
        .toArray()
      const totalRevenue = purchases.reduce(
        (sum, p) => sum + (p.totalAmount || 0),
        0,
      )

      return {
        productsCount,
        purchasesCount,
        totalRevenue,
      }
    } catch (error) {
      console.error('Erreur statistiques:', error)
      return { productsCount: 0, purchasesCount: 0, totalRevenue: 0 }
    }
  }

  // EXPORT SÉLECTIF (par boutique) - SANS SHOPINFO
  async exportData(selectedProductIds = [], selectedPurchaseIds = []) {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) throw new Error('Aucune session active')

      const productsQuery =
        selectedProductIds.length > 0
          ? db.products
              .where('id')
              .anyOf(selectedProductIds)
              .and((p) => p.shopId === shopId)
          : db.products.where('shopId').equals(shopId)

      const purchasesQuery =
        selectedPurchaseIds.length > 0
          ? db.purchases
              .where('id')
              .anyOf(selectedPurchaseIds)
              .and((p) => p.shopId === shopId)
          : db.purchases.where('shopId').equals(shopId)

      const products = await productsQuery.toArray()
      const purchases = await purchasesQuery.toArray()

      return {
        exportDate: new Date().toISOString(),
        shopId, // Gardé pour référence mais shopInfo n'est pas exporté
        products,
        purchases,
        metadata: {
          version: '3.0.0',
          totalProducts: products.length,
          totalPurchases: purchases.length,
        },
      }
    } catch (error) {
      console.error('Erreur export:', error)
      throw error
    }
  }

  // FORMATAGE BOUTIQUE (supprime toutes les données de la boutique actuelle)
  async formatShop() {
    try {
      const shopId = this.getCurrentShopId()
      if (!shopId) return false

      // Supprimer tous les produits de la boutique
      await db.products.where('shopId').equals(shopId).delete()

      // Supprimer tous les achats de la boutique
      await db.purchases.where('shopId').equals(shopId).delete()

      // Supprimer les métadonnées de la boutique
      const metadataKeys = await db.metadata.toArray()
      const shopMetadataKeys = metadataKeys
        .filter((m) => m.key.startsWith(`${shopId}_`))
        .map((m) => m.key)

      await db.metadata.bulkDelete(shopMetadataKeys)

      console.log(`[DexieService] Boutique ${shopId} formatée`)
      return true
    } catch (error) {
      console.error('Erreur formatage boutique:', error)
      return false
    }
  }

  // NETTOYAGE COMPLET (admin uniquement - pour tests)
  // async clearAllData() {
  //   try {
  //     await db.products.clear()
  //     await db.purchases.clear()
  //     await db.metadata.clear()
  //     await db.shopInfo.clear()
  //     return true
  //   } catch (error) {
  //     console.error('Erreur nettoyage:', error)
  //     return false
  //   }
  // }
}

export default new DexieService()
